import React from 'react';
import './styles/whychooseus.css';

const USP_ITEMS = [
    {
        color: '#1565c0',
        tag: 'Background Verified',
        title: 'Verified Artisan Network',
        desc: 'Every artisan is personally vetted — skills-tested, background-checked, and trained to meet B2B export standards before joining the platform.',
        icon: (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                <polyline points="9 12 11 14 15 10"/>
            </svg>
        ),
    },
    {
        color: '#0097a7',
        tag: 'From Sketch to Sample',
        title: 'Custom Design Capability',
        desc: 'Submit a mood board, sketch, or product spec — our design team translates it into a manufacturable, market-ready sample with your IP fully protected.',
        icon: (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="13.5" cy="6.5" r="0.5" fill="currentColor"/>
                <circle cx="17.5" cy="10.5" r="0.5" fill="currentColor"/>
                <circle cx="8.5" cy="7.5" r="0.5" fill="currentColor"/>
                <circle cx="6.5" cy="12.5" r="0.5" fill="currentColor"/>
                <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"/>
            </svg>
        ),
    },
    {
        color: '#7b1fa2',
        tag: 'ISO-Grade Checks',
        title: 'Multi-Stage Quality Control',
        desc: 'Raw-material inspection, in-line production checks, and a final pre-dispatch audit ensure every shipment arrives exactly as specified.',
        icon: (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 2 7 12 12 22 7 12 2"/>
                <polyline points="2 17 12 22 22 17"/>
                <polyline points="2 12 12 17 22 12"/>
            </svg>
        ),
    },
    {
        color: '#e47911',
        tag: 'MOQ from 50 Units',
        title: 'Bulk Order Flexibility',
        desc: 'Scale from 50-unit sampling runs to full mass production. Mix SKUs within a category to reach volume thresholds without sacrificing variety.',
        icon: (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                <line x1="16.5" y1="9.4" x2="7.5" y2="4.21"/>
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                <line x1="12" y1="22.08" x2="12" y2="12"/>
            </svg>
        ),
    },
    {
        color: '#2e7d32',
        tag: 'DHL & FedEx Integrated',
        title: 'Pan-India Delivery',
        desc: 'Warehouse hubs in Jaipur, Moradabad, and Kolkata with DHL & FedEx integrations for tracked, insured doorstep delivery across 50+ countries.',
        icon: (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                <rect x="1" y="3" width="15" height="13" rx="1"/>
                <path d="M16 8h4l3 5v3h-7V8z"/>
                <circle cx="5.5" cy="18.5" r="2.5"/>
                <circle cx="18.5" cy="18.5" r="2.5"/>
            </svg>
        ),
    },
    {
        color: '#c62828',
        tag: '7 Days a Week',
        title: 'Dedicated Account Support',
        desc: 'One point of contact from first enquiry to repeat reorder. Your account manager coordinates sampling, production timelines, and last-mile shipping.',
        icon: (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 18v-6a9 9 0 0 1 18 0v6"/>
                <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3z"/>
                <path d="M3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/>
            </svg>
        ),
    },
];

const STATS = [
    { value: '500+',   label: 'Verified Artisans' },
    { value: '5,000+', label: 'Products Listed' },
    { value: '50+',    label: 'Countries Served' },
    { value: '98%',    label: 'Client Satisfaction' },
];

const CheckIcon = () => (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12"/>
    </svg>
);

const WhyChooseUs: React.FC = () => (
    <section className="wcu-section">

        {/* ══ Zone 1: Dark stats band ══ */}
        <div className="wcu-band">
            <div className="wcu-band-texture" aria-hidden="true" />

            <div className="wcu-container">

                {/* Header */}
                <div className="wcu-band-header">
                    <span className="wcu-eyebrow">Why Choose Us</span>
                    <h2 className="wcu-title">The ArteCraft Advantage</h2>
                    <span className="wcu-title-underline" />
                    <p className="wcu-subtitle">
                        A marketplace built for serious B2B buyers — verified sourcing,
                        bespoke manufacturing, and dedicated support at every stage.
                    </p>
                </div>

                {/* Stats */}
                <div className="wcu-stats">
                    {STATS.map(stat => (
                        <div className="wcu-stat" key={stat.label}>
                            <span className="wcu-stat-value">{stat.value}</span>
                            <span className="wcu-stat-label">{stat.label}</span>
                        </div>
                    ))}
                </div>

            </div>
        </div>

        {/* ══ Zone 2: Feature cards ══ */}
        <div className="wcu-features">
            <div className="wcu-container">

                <div className="wcu-features-header">
                    <span className="wcu-features-eyebrow">Our Commitments</span>
                    <h3 className="wcu-features-title">Six reasons buyers trust Arte Craft India</h3>
                    <p className="wcu-features-subtitle">
                        From first enquiry to final delivery — every step is engineered around your business needs.
                    </p>
                </div>

                <div className="wcu-grid">
                    {USP_ITEMS.map(item => (
                        <div
                            className="wcu-card"
                            key={item.title}
                            style={{ '--wcu-accent': item.color } as React.CSSProperties}
                        >
                            {/* Corner glow */}
                            <div className="wcu-card-glow" aria-hidden="true" />

                            {/* Icon */}
                            <div
                                className="wcu-icon-wrap"
                                style={{
                                    background: `${item.color}16`,
                                    border: `1px solid ${item.color}35`,
                                    color: item.color,
                                }}
                            >
                                {item.icon}
                            </div>

                            <h3 className="wcu-card-title">{item.title}</h3>
                            <p className="wcu-card-desc">{item.desc}</p>

                            {/* Tag */}
                            <div className="wcu-card-tag">
                                <CheckIcon />
                                {item.tag}
                            </div>
                        </div>
                    ))}
                </div>

            </div>
        </div>

    </section>
);

export default WhyChooseUs;
